import Backdrop from "./Backdrop";
import Button from "./Button";
import ModalWrapper from "./ModalWrapper";
import Spacer from "./Spacer";
import sadEmojiGroup from "assets/images/sadEmojiGroup.svg";
import styled from "styled-components";

const Wrapper = styled(Backdrop)`
  z-index: ${({ cover }) => (cover ? "12" : "5")};
  max-width: 100vw;
  backdrop-filter: ${({ blur }) => (blur ? "blur(10px)" : "blur(0px)")};

  .card {
    width: 480px;
    padding: 48px;
    background-color: var(--white);
    border-radius: 16px;

    .image {
      margin-bottom: 24px;
    }

    .title {
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }

    .description {
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
      line-height: 20px;
    }
  }

  .actionBtns {
    gap: 24px;
  }

  .btn {
    height: 48px;
    width: 100%;
    border-radius: 8px;
    padding: 0 24px;
    text-transform: capitalize;

    &.actionBtn {
      color: var(--white);
      &:hover {
        background-color: var(--primary_light);
      }
    }

    &.cancel {
      &:not([disabled]):hover {
        background-color: var(--background);
        color: var(--grey_1);
        img {
          filter: ${({ invertOnHover }) =>
            invertOnHover ? `brightness(0) invert(0)` : ""};
        }
      }
    }

    &.danger {
      background-color: var(--danger);

      &:hover {
        background-color: #c80100;
      }

      &:active {
        background-color: #c80100;
      }

      &:disabled {
        background-color: var(--danger_light);
      }
    }

    &.info {
      background-color: var(--secondary);
    }

    &.secondary {
      background-color: var(--primary);
    }

    &:disabled {
      background-color: var(--body_text);
    }
  }

  &.warning .card {
    background-color: #fffcf0;
  }

  @media screen and (max-width: 768px) {
    .card {
      width: calc(100% - 24px);
      padding: 24px;

      .title {
        font-size: 16px;
        line-height: 20px;
      }

      .description {
        font-size: 14px;
        line-height: 20px;
      }

      .btn {
        min-width: unset;
        width: calc(50% - 24px);
      }
    }
  }
`;

/**
 * ConfirmModal component displays a modal with a confirmation message and action buttons.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.id="confirmModal" - The ID of the modal.
 * @param {string} [props.title] - The title of the modal.
 * @param {string} [props.action] - The action description.
 * @param {string} [props.description] - The description of the modal.
 * @param {string} [props.actionText] - The text for the action button.
 * @param {Function} [props.callback] - The callback function to be executed on action button click.
 * @param {Function} [props.onCancelCallback] - The callback function to be executed on cancel button click.
 * @param {string} [props.image] - The image source for the modal header.
 * @param {boolean} [props.warning] - Indicates if the modal is a warning modal.
 * @param {string} [props.type="danger"] - The type of the action button.
 * @param {boolean} [props.blur] - Indicates if the modal should have a backdrop blur effect.
 * @param {boolean} [props.actionOnly] - Indicates if only the action button should be displayed.
 * @param {boolean} [props.noHeaderImages] - Indicates if the modal header images should be hidden.
 * @param {boolean} [props.disabled] - Indicates if the action button should be disabled.
 * @param {boolean} [props.cover=true] - Indicates if the modal should cover the entire screen.
 * @param {string}  [props.onCancelText] - The text for the cancel button.
 * @param {boolean} [props.allowClickOutside] - Indicates if the modal should close when clicked outside.
 * @returns {JSX.Element} The ConfirmModal component.
 */
const ConfirmModal = ({
  id = "confirmModal",
  title,
  action,
  description,
  actionText,
  callback,
  onCancelCallback,
  onCancelText,
  image,
  warning,
  type = "danger",
  blur,
  actionOnly,
  noHeaderImages,
  disabled,
  cover = true,
  allowClickOutside,
}) => {
  const handleAction = async (e) => {
    e.stopPropagation();
    if (!callback) {
      return;
    }
    document.querySelector(`#${id}.confirmModal`).classList.remove("show");
    await callback();
  };

  return (
    <Wrapper
      blur={blur}
      cover={cover}
      id={id}
      className={`confirmModal flexRow justifyEnd${warning ? " warning" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        if (allowClickOutside) {
          document
            .querySelector(`#${id}.confirmModal`)
            .classList.remove("show");
        } else {
          return;
        }
      }}
    >
      <ModalWrapper>
        <div className="card flexColumn alignCenter">
          {!noHeaderImages && (
            <img src={image || sadEmojiGroup} alt="." className="image" />
          )}
          {title ? (
            <h5 className="colorGrey1 textCenter title">{title}</h5>
          ) : (
            <h5 className="colorGrey1 textCenter title">
              Are you sure you want to
              <br />
              {action}?
            </h5>
          )}
          <Spacer y={0.6} />
          <p className="l2 colorBodyText textCenter description">
            {description}
          </p>
          <Spacer y={2.4} />
          <div className="flexRow justifyCenter fullWidth actionBtns">
            {!actionOnly && (
              <Button
                text={onCancelText || "Cancel"}
                className="btn cancel flexRow alignCenter justifyCenter"
                color="var(--grey_1)"
                bd="var(--grey_10)"
                bg="#fffff"
                onClick={(e) => {
                  e.stopPropagation();
                  document
                    .querySelector(`#${id}.confirmModal`)
                    .classList.remove("show");
                  onCancelCallback && onCancelCallback();
                }}
                disabled={disabled}
              />
            )}
            <button
              onClick={handleAction}
              className={`btn actionBtn flexRow alignCenter justifyCenter ${type}`}
              disabled={disabled}
            >
              {actionText}
            </button>
          </div>
        </div>
      </ModalWrapper>
    </Wrapper>
  );
};

export default ConfirmModal;
